import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { getHistoryPost } from '../services/wordpress';
import { NewsItem } from '../types';
import { ArrowLeft, Loader2, Calendar, Share2, MessageCircle, Facebook, Twitter, Link as LinkIcon } from 'lucide-react';
import { safeHTML } from '../utils/safeHTML';
import './SinglePost.css';

import { useNavigate } from 'react-router-dom';

const HistoryPage: React.FC = () => {
  const navigate = useNavigate();
  const [post, setPost] = useState<NewsItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    async function loadHistory() {
      setLoading(true);
      const data = await getHistoryPost();
      setPost(data);
      setLoading(false);
    }
    loadHistory();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleShare = (platform: 'fb' | 'tw' | 'wa' | 'copy') => {
    const url = window.location.href;
    const title = post?.title || "Sejarah Lakpesdam PBNU";
    
    switch (platform) {
      case 'fb': window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank', 'noopener,noreferrer'); break;
      case 'tw': window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`, '_blank', 'noopener,noreferrer'); break;
      case 'wa': window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(title + ' ' + url)}`, '_blank', 'noopener,noreferrer'); break;
      case 'copy':
        navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        break;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-white">
        <Loader2 className="animate-spin text-emerald-700" size={48} />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4">
        <h2 className="text-2xl font-bold text-slate-800">Halaman Sejarah Tidak Ditemukan</h2>
        <button onClick={() => navigate(-1)} className="text-emerald-700 font-semibold hover:underline">Kembali</button>
      </div>
    );
  }

  return (
    <article className="SinglePost_page">
      <Helmet>
        <title>Sejarah Lakpesdam PBNU</title>
        <meta name="description" content="Perjalanan dan sejarah Lembaga Kajian dan Pengembangan Sumber Daya Manusia PBNU sejak 1985." />
        <meta property="og:title" content="Sejarah Lakpesdam PBNU" />
        <meta property="og:description" content="Perjalanan dan sejarah Lakpesdam PBNU sejak 1985." />
        <meta property="og:image" content="https://lakpesdam.or.id/assets/og-image.png" />
        <meta property="og:type" content="article" />
      </Helmet>
      <div className="SinglePost_container">
        <button onClick={() => navigate(-1)} className="SinglePost_backBtn mb-8">
          <ArrowLeft size={20} /> Kembali ke Tentang Kami
        </button>

        <header className="SinglePost_header">
          <span className="SinglePost_category">PROFIL</span>
          {/* eslint-disable-next-line jsx-a11y/heading-has-content */}
          <h1 className="SinglePost_title" {...safeHTML(post.title)} aria-label={post.title.replace(/<[^>]+>/g, '').trim()} />
          <div className="SinglePost_meta">
            <div className="SinglePost_metaItem">
              <Calendar size={16} />
              <span>Pembaruan Terakhir: {post.date}</span>
            </div>
          </div>
        </header>

        {post.imageUrl && (
          <div className="SinglePost_featuredImageWrapper">
            <img src={post.imageUrl} alt={post.title} className="SinglePost_featuredImage" loading="lazy" width="800" height="400" />
          </div>
        )}

        <div className="SinglePost_content" {...safeHTML(post.content || post.excerpt)} />

        <div className="SinglePost_shareSection">
          <div className="SinglePost_shareTitle">
            <Share2 size={18} />
            <span>Bagikan Sejarah Ini</span>
          </div>
          <div className="SinglePost_shareButtons">
            <button onClick={() => handleShare('wa')} aria-label="Bagikan ke WhatsApp" className="SinglePost_shareBtn SinglePost_wa"><MessageCircle size={20} /></button>
            <button onClick={() => handleShare('fb')} aria-label="Bagikan ke Facebook" className="SinglePost_shareBtn SinglePost_fb"><Facebook size={20} /></button>
            <button onClick={() => handleShare('tw')} aria-label="Bagikan ke Twitter" className="SinglePost_shareBtn SinglePost_tw"><Twitter size={20} /></button>
            <button onClick={() => handleShare('copy')} aria-label="Salin tautan" className="SinglePost_shareBtn SinglePost_copy">
              <LinkIcon size={20} />
              {copied && <span className="SinglePost_copyTooltip">Tautan Disalin!</span>}
            </button>
          </div>
        </div>

        <div className="SinglePost_footer">
           <button onClick={() => navigate(-1)} className="SinglePost_backBtn">
             <ArrowLeft size={20} /> Kembali
           </button>
        </div>
      </div>
    </article>
  );
};

export default HistoryPage;
